package com.ijoin.data.repository

import com.ijoin.data.api.LegalApi
import com.ijoin.data.model.AcceptLegalDocumentsRequest
import com.ijoin.data.model.LegalAcceptanceStatus
import com.ijoin.data.model.LegalDocument
import com.ijoin.data.model.LegalDocumentType

/**
 * Repository for legal document operations.
 */
class LegalRepository(
    private val api: LegalApi = LegalApi()
) {
    /**
     * Get document content by type.
     */
    suspend fun getDocument(type: LegalDocumentType): Result<LegalDocument> =
        api.getDocument(type.apiName)

    /**
     * Get document content by type name.
     */
    suspend fun getDocument(typeName: String): Result<LegalDocument> =
        api.getDocument(typeName)

    /**
     * Get current versions of all legal documents.
     */
    suspend fun getCurrentVersions(): Result<Map<String, String>> =
        api.getCurrentVersions()

    /**
     * Get user's acceptance status for all documents.
     */
    suspend fun getAcceptanceStatus(): Result<LegalAcceptanceStatus> =
        api.getAcceptanceStatus()

    /**
     * Accept legal documents.
     */
    suspend fun acceptDocuments(types: List<LegalDocumentType>): Result<Unit> {
        val request = AcceptLegalDocumentsRequest(
            documentTypes = types.map { it.apiName }
        )
        return api.acceptDocuments(request).map { }
    }

    /**
     * Accept registration documents (Terms, Privacy, Refund).
     */
    suspend fun acceptRegistrationDocuments(): Result<Unit> =
        acceptDocuments(LegalDocumentType.REGISTRATION_TYPES)

    /**
     * Accept organizer terms.
     */
    suspend fun acceptOrganizerTerms(): Result<Unit> =
        acceptDocuments(listOf(LegalDocumentType.ORGANIZER_TERMS))

    /**
     * Accept AI data processing consent.
     */
    suspend fun acceptAiConsent(): Result<Unit> =
        acceptDocuments(listOf(LegalDocumentType.AI_DATA_CONSENT))

    /**
     * Check if user has accepted AI data processing consent.
     */
    suspend fun hasAcceptedAiConsent(): Result<Boolean> {
        return getAcceptanceStatus().map { status ->
            status.acceptedStatus["AI_DATA_CONSENT"] ?: true
        }
    }

    /**
     * Withdraw AI data processing consent.
     */
    suspend fun withdrawAiConsent(): Result<Unit> =
        api.withdrawAiConsent().map { }

    /**
     * Check if user can publish events (has accepted organizer terms).
     */
    suspend fun canPublishEvent(): Result<Boolean> =
        api.canPublishEvent().map { it.canPublish }
}
